import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { balance, campaignsList } from 'src/Router/routes'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { useMetadataQuery } from 'src/Redux/metadataApi'
import dayJs from 'src/Helpers/dayjs'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useHint } from 'src/Hooks/useHint'
import { useAppDispatch } from 'src/Hooks/redux'
import { getIsUserReferral } from 'src/Redux/auth-process/userSlice/selectors'
import { getValueOrZero } from 'src/Constants/functions'
import { refetchUsersMe } from 'src/Redux/userInfoApi'
import { usePaymentCompletedEvents } from 'src/Hooks/usePaymentCompletedEvents'
import { OrderStatus } from 'src/Redux/artistOrderApi'
import { useOrderPaymentPolling } from 'src/Hooks/useOrderPaymentPolling'
import { useRetryCampaignPayment } from 'src/Hooks/useCampaignPayment'
import { markReturnedFromPaymentProvider } from 'src/Utils/paymentReturnMarks'
import {
  PAYMENT_FAILED_TEXT,
  PAYMENT_IN_PROGRESS_TEXT,
} from 'src/Constants/paymentStatusTexts'

const DEFAULT_BONUS = 20
const META_MAX_POLL_TICKS = 30
const QUICK_CHECKOUT_KEY = 'quick_checkout'
const QUICK_CHECKOUT_SKIP_BONUS_VALUE = '4'

interface UseSuccessPage {
  isLoading: boolean
  isTimedOut: boolean
  isFailed: boolean
  isRetrying: boolean
  isSuccessPageVisible: boolean
  isBonusActive: boolean
  bonusAmount: number
  onlyDate: string
  title: string
  text: string
  onCampaignsButtonClick: () => void
  onBalanceButtonClick: () => void
  onTryAnotherMethodClick: () => void
}

interface UseSuccessPageProps {
  startDate?: string | Date
  platformName?: CampaignPlatformTypeEnum
}

interface UrlParams {
  isLowReachCampaign: boolean
  paymentStatus: string | null
  startDate: string | null
}

interface BonusConfig {
  isCheckoutBonus: boolean
  bonusAmount: number
}

const parseUrlParams = (search: URLSearchParams): UrlParams => ({
  isLowReachCampaign: Boolean(search.get('isLowReachCampaign')),
  paymentStatus: search.get('paymentStatus'),
  startDate: search.get('startDate'),
})

const normalizeStartDate = (rawDate: string | null): string | null => {
  if (!rawDate) {
    return null
  }
  return rawDate.includes(' ') ? rawDate.replace(/ /g, '+') : rawDate
}

const formatStartDate = (
  urlStartDate: string | null,
  fallbackDate?: string | Date,
): string => {
  const currentStartDate = urlStartDate || fallbackDate
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const timezone =
    detectedTimezone && detectedTimezone !== 'Etc/Unknown'
      ? detectedTimezone
      : 'UTC'
  try {
    return dayJs(currentStartDate).tz(timezone).format('MMMM Do')
  } catch {
    return dayJs(currentStartDate).tz('UTC').format('MMMM Do')
  }
}

const calculateBonusConfig = (
  paidCampaignsAmount: number,
  isUserReferral: boolean,
  isLowReachCampaign: boolean,
  quickCheckoutValue: string | null,
): BonusConfig => {
  const isNextTimeBonusReceived = paidCampaignsAmount === 1 && !isUserReferral

  return {
    isCheckoutBonus:
      isNextTimeBonusReceived &&
      !isLowReachCampaign &&
      quickCheckoutValue !== QUICK_CHECKOUT_SKIP_BONUS_VALUE,
    bonusAmount: DEFAULT_BONUS,
  }
}

const generateSuccessMessage = (
  isLoading: boolean,
  isTimedOut: boolean,
  isFailed: boolean,
  onlyDate: string,
): { title: string; text: string } => {
  if (isFailed) {
    return { title: 'Payment failed', text: PAYMENT_FAILED_TEXT }
  }

  if (isTimedOut) {
    return {
      title: 'Payment is still processing',
      text: PAYMENT_IN_PROGRESS_TEXT,
    }
  }

  if (isLoading) {
    return {
      title: 'Processing...',
      text: 'Your payment is currently being processed. Stay informed about the payment status on the Balance page.',
    }
  }

  return {
    title: 'Campaign scheduled 🎉',
    text: `Your track starts its journey on ${onlyDate}`,
  }
}

const clearQuickCheckout = (): void => {
  localStorage.removeItem(QUICK_CHECKOUT_KEY)
}

export const useSuccessPage = ({
  startDate,
  platformName,
}: UseSuccessPageProps): UseSuccessPage => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const params = useParams()
  const { search } = useCustomSearchParams()
  const { setIsOpen } = useHint()

  const isUserReferral = useSelector(getIsUserReferral)

  const initialized = useRef(false)
  const { handlePaymentCompletedEvents } = usePaymentCompletedEvents()

  const urlParams = useMemo(() => parseUrlParams(search), [search])
  const quickCheckoutValue = localStorage.getItem(QUICK_CHECKOUT_KEY)

  const isSuccessPageVisible = urlParams.paymentStatus === 'success'
  const isMetaCampaign = platformName === CampaignPlatformTypeEnum.META

  const {
    isFetching: isMetadataFetching,
    data,
    refetch,
  } = useMetadataQuery(null, {
    refetchOnMountOrArgChange: 1,
  })

  const campaignId = Number(params.number)
  const { retryPayment, isRetrying } = useRetryCampaignPayment()

  const {
    isAwaitingPayment,
    isPaid,
    isFailed,
    failedOrderId,
    isTimedOut,
    isResolved,
  } = useOrderPaymentPolling({
    campaignId,
    enabled: isSuccessPageVisible,
    platform: platformName,
    awaitingStatuses: isMetaCampaign
      ? [OrderStatus.Checkout, OrderStatus.PaymentSent]
      : [OrderStatus.Checkout],
    maxPollTicks: isMetaCampaign ? META_MAX_POLL_TICKS : void 0,
  })

  const isLoading =
    !isFailed && (isMetadataFetching || isAwaitingPayment || !isResolved)
  const isConfirmedSuccess =
    isSuccessPageVisible && !isLoading && !isTimedOut && !isFailed

  const paidCampaignsAmount = useMemo(
    () =>
      getValueOrZero(data?.paymentsCountByStatus.completed) +
      getValueOrZero(data?.paymentsCountByStatus.payed),
    [data],
  )

  const bonusConfig = useMemo(
    () =>
      calculateBonusConfig(
        paidCampaignsAmount,
        isUserReferral,
        urlParams.isLowReachCampaign,
        quickCheckoutValue,
      ),
    [
      paidCampaignsAmount,
      isUserReferral,
      urlParams.isLowReachCampaign,
      quickCheckoutValue,
    ],
  )

  const onlyDate = useMemo(() => {
    const normalizedDate = normalizeStartDate(urlParams.startDate)
    return formatStartDate(normalizedDate, startDate)
  }, [urlParams.startDate, startDate])

  const { title, text } = useMemo(
    () => generateSuccessMessage(isLoading, isTimedOut, isFailed, onlyDate),
    [isLoading, isTimedOut, isFailed, onlyDate],
  )

  const onCampaignsButtonClick = useCallback((): void => {
    navigate(campaignsList)
    setIsOpen()
    clearQuickCheckout()
  }, [navigate, setIsOpen])

  const onBalanceButtonClick = useCallback((): void => {
    navigate(balance)
  }, [navigate])

  const onTryAnotherMethodClick = useCallback((): void => {
    if (!platformName || failedOrderId === void 0) {
      return
    }
    retryPayment(platformName, campaignId, failedOrderId)
  }, [campaignId, failedOrderId, platformName, retryPayment])

  useEffect(() => {
    if (isSuccessPageVisible && platformName && campaignId) {
      markReturnedFromPaymentProvider(platformName, campaignId)
    }
  }, [campaignId, isSuccessPageVisible, platformName])

  useEffect(() => {
    if (!isAwaitingPayment) {
      refetch()
    }
  }, [isAwaitingPayment, isSuccessPageVisible, refetch])

  useEffect(() => {
    if (isPaid) {
      void dispatch(refetchUsersMe())
    }
  }, [dispatch, isPaid])

  useEffect(() => {
    if (isConfirmedSuccess && !initialized.current && platformName) {
      initialized.current = true
      handlePaymentCompletedEvents({ platformName })
    }
  }, [
    initialized,
    isConfirmedSuccess,
    handlePaymentCompletedEvents,
    platformName,
  ])

  return {
    onlyDate,
    isLoading,
    isTimedOut,
    isFailed,
    isRetrying,
    isSuccessPageVisible,
    title,
    text,
    onCampaignsButtonClick,
    onBalanceButtonClick,
    onTryAnotherMethodClick,
    isBonusActive: bonusConfig.isCheckoutBonus,
    bonusAmount: bonusConfig.bonusAmount,
  }
}
